import { FlaskConical } from "lucide-react";
import { Link } from "react-router-dom";
import type { SynthesisBatchListRow } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatCost, formatRelative } from "@/lib/format";
import { useSynthesisBatches } from "@/lib/SynthesisBatchesContext";
import { useDeveloperMode } from "@/lib/uiMode";
import { DeveloperModeOnly } from "@/components/DeveloperModeOnly";
import { StatusPill } from "@/pages/Recordings";

const SUMMARY = "What a synthesis batch read, what the model said, and what became a belief.";

/**
 * UI-24 — the processing view's landing page: the backlog, the most recent batch, and a table
 * of recent ones. The navigator panel (`AppShell`'s `processingNavigator`) already lists
 * batches for quick switching; this is the fuller table with the columns a compact row cannot
 * carry, and it is what a `/processing` visit with nothing selected shows.
 */
export function ProcessingPage() {
  const developerMode = useDeveloperMode();
  const { batches, loading, problem } = useSynthesisBatches();

  if (!developerMode) return <DeveloperModeOnly label="Insight processing" summary={SUMMARY} />;

  return (
    <div className="flex flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header className="flex flex-col gap-1">
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Insight processing
        </h1>
        <p className="max-w-[70ch] text-[length:var(--text-md)] text-[var(--text-secondary)] [text-wrap:pretty]">
          {SUMMARY}
        </p>
      </header>

      {problem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      {loading && !batches && (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      )}

      {batches && (
        <>
          <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            {batches.waitingCount > 0
              ? `${batches.waitingCount} approved ${batches.waitingCount === 1 ? "insight" : "insights"} waiting for the next batch.`
              : "Nothing waiting — every approved insight has been through a batch."}
          </p>

          {batches.batches.length === 0 ? (
            <Empty />
          ) : (
            <BatchTable rows={batches.batches} />
          )}
        </>
      )}
    </div>
  );
}

function BatchTable({ rows }: { rows: SynthesisBatchListRow[] }) {
  return (
    <div className="overflow-hidden rounded-[var(--radius-xl)] border border-[var(--border-subtle)]">
      <table className="w-full text-left text-[length:var(--text-sm)]">
        <thead>
          <tr className="border-b border-[var(--border-subtle)] bg-[var(--surface-1)] text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
            <th className="px-[var(--space-4)] py-[var(--space-2)]">Batch</th>
            <th className="px-[var(--space-4)] py-[var(--space-2)]">Run</th>
            <th className="px-[var(--space-4)] py-[var(--space-2)]">Insights</th>
            <th className="px-[var(--space-4)] py-[var(--space-2)]">Applied</th>
            <th className="px-[var(--space-4)] py-[var(--space-2)]">Not applied</th>
            <th className="px-[var(--space-4)] py-[var(--space-2)]">Engine</th>
            <th className="px-[var(--space-4)] py-[var(--space-2)]">Cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--surface-1)]">
              <td className="px-[var(--space-4)] py-[var(--space-3)]">
                <Link to={`/processing/${row.id}`} className="font-medium text-[var(--text-primary)] hover:underline">
                  {formatRelative(row.completedAt ?? row.createdAt)}
                </Link>
                {row.error && (
                  <p className="mt-0.5 max-w-[36ch] truncate text-[length:var(--text-xs)] text-[var(--destructive)]">
                    {row.error}
                  </p>
                )}
              </td>
              <td className="px-[var(--space-4)] py-[var(--space-3)]">
                <StatusPill status={row.runStatus} />
                {row.currentStep && row.runStatus !== "done" && (
                  <span className="ml-2 font-[family-name:var(--font-mono)] text-[length:var(--text-2xs)] text-[var(--text-muted)]">
                    {row.currentStep}
                  </span>
                )}
              </td>
              <td className="px-[var(--space-4)] py-[var(--space-3)] tabular-nums text-[var(--text-secondary)]">
                {row.insightCount}
              </td>
              <td className="px-[var(--space-4)] py-[var(--space-3)] tabular-nums text-[var(--text-secondary)]">
                {row.appliedCount}
              </td>
              <td className="px-[var(--space-4)] py-[var(--space-3)] tabular-nums">
                <span className={cn(row.droppedCount > 0 ? "text-[var(--warning)]" : "text-[var(--text-muted)]")}>
                  {row.droppedCount}
                </span>
              </td>
              <td className="px-[var(--space-4)] py-[var(--space-3)] text-[var(--text-secondary)]">{row.engine}</td>
              <td className="px-[var(--space-4)] py-[var(--space-3)] tabular-nums text-[var(--text-secondary)]">
                {formatCost(row.costUsd)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty() {
  return (
    <div className="flex flex-col items-center justify-center rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] bg-[var(--surface-glass)] px-[var(--space-8)] py-[var(--space-12)] text-center backdrop-blur-[var(--blur-sm)]">
      <div className="mb-[var(--space-4)] flex h-12 w-12 items-center justify-center rounded-[var(--radius-full)] bg-[var(--surface-2)]">
        <FlaskConical size={22} strokeWidth={2} className="text-[var(--text-muted)]" />
      </div>
      <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">No batches yet</p>
      <p className="mt-1 max-w-[46ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        A batch opens the moment an approved insight has none waiting for it.
      </p>
    </div>
  );
}
